// The ticks of a Multi-select checkbox list, and the rules every such list shares. Only ids the field still has
// count: a stored id with no option is neither ticked nor counted as a change. Select all is ticked when every
// option is and a dash when some are; ticking it ticks every option the field has now and unticking it clears them
// all, so an option added later is never ticked on its own. Every change applies to the latest ticks, so two clicks
// before a render both count.
import {useRef, useState} from 'react';
import {parseMultiSelectValue, sameSelection, toMultiSelectValue} from '../forecast-app/shared/util/customFieldValueValidation';

export type MultiSelectSelection = {
	// The ticked ids, in the option list's order.
	chosen: string[];
	// The ticked ids as the value to save: their JSON array, or null when none is ticked.
	chosenValue: string | null;
	selectAll: boolean | 'indeterminate';
	toggle: (key: string) => void;
	toggleAll: () => void;
	// Shows a stored value again, dropping the ticks.
	reset: (value: string | null | undefined) => void;
	// Whether the latest ticks differ from baseline, in any order. Read through a ref, so it is right during the
	// unmount that follows the last render.
	changedFrom: (baseline: ReadonlyArray<string>) => boolean;
	// The latest ticks as the value to save, read the same way.
	latestValue: () => string | null;
};

// The ids among keys that a stored value ticks, in the order of keys.
export const chosenKeys = (value: string | null | undefined, keys: ReadonlyArray<string>): string[] => {
	const stored = parseMultiSelectValue(value);
	return keys.filter(key => stored.includes(key));
};

// keys are the field's option ids as a stored value holds them, in the option list's order.
export const useMultiSelectSelection = (
	value: string | null | undefined,
	keys: ReadonlyArray<string>
): MultiSelectSelection => {
	const [selection, setSelection] = useState<string[]>(() => parseMultiSelectValue(value));
	const chosen = keys.filter(key => selection.includes(key));
	const latest = useRef(chosen);
	latest.current = chosen;

	return {
		chosen,
		chosenValue: toMultiSelectValue(chosen),
		selectAll: chosen.length === 0 ? false : chosen.length === keys.length ? true : 'indeterminate',
		toggle: key => setSelection(current => keys.filter(k => (k === key ? !current.includes(k) : current.includes(k)))),
		toggleAll: () => setSelection(current => (keys.every(k => current.includes(k)) ? [] : [...keys])),
		// Keeps the same selection when the stored one is no different, so showing it again renders nothing.
		reset: stored =>
			setSelection(current => {
				const next = parseMultiSelectValue(stored);
				return sameSelection(current, next) ? current : next;
			}),
		changedFrom: baseline => !sameSelection(latest.current, baseline),
		latestValue: () => toMultiSelectValue(latest.current),
	};
};
